import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { Play, Share2, Users, Trophy, Search, Film, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const EVENT_TYPES = {
  goal: { label: "Goal", color: "bg-green-100 text-green-800 border-green-200" },
  assist: { label: "Assist", color: "bg-blue-100 text-blue-800 border-blue-200" },
  save: { label: "Save", color: "bg-purple-100 text-purple-800 border-purple-200" },
  tackle: { label: "Tackle", color: "bg-amber-100 text-amber-800 border-amber-200" },
  highlight: { label: "Highlight", color: "bg-[#0C2E3C]/10 text-[#0C2E3C] border-[#0C2E3C]/20" },
  full_game: { label: "Full Game", color: "bg-gray-100 text-gray-700 border-gray-200" },
};

const getYouTubeId = (url) => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

const getVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
};

const VideoEmbed = ({ clip, autoTrack }) => {
  useEffect(() => {
    if (autoTrack && clip.highlight_id) {
      axios.post(`${API}/highlights/${clip.highlight_id}/view`).catch(() => {});
    }
  }, [autoTrack, clip.highlight_id]);

  const ytId = getYouTubeId(clip.video_url);
  const vimeoId = getVimeoId(clip.video_url);

  if (ytId) {
    return (
      <div className="relative w-full pt-[56.25%]">
        <iframe src={`https://www.youtube.com/embed/${ytId}`} className="absolute inset-0 w-full h-full" allowFullScreen title={clip.title} />
      </div>
    );
  }
  if (vimeoId) {
    return (
      <div className="relative w-full pt-[56.25%]">
        <iframe src={`https://player.vimeo.com/video/${vimeoId}`} className="absolute inset-0 w-full h-full" allowFullScreen title={clip.title} />
      </div>
    );
  }
  return (
    <div className="relative w-full pt-[56.25%] bg-black">
      <video src={clip.video_url} controls className="absolute inset-0 w-full h-full" />
    </div>
  );
};

const HighlightsPage = () => {
  const { playerName } = useParams();
  const [searchParams] = useSearchParams();
  const [highlights, setHighlights] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [selectedClip, setSelectedClip] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (playerName) {
          const res = await axios.get(`${API}/highlights/public/player/${encodeURIComponent(playerName)}`);
          setHighlights(res.data.highlights);
        } else {
          const [hlRes, plRes] = await Promise.all([
            axios.get(`${API}/highlights/public`),
            axios.get(`${API}/highlights/players`),
          ]);
          setHighlights(hlRes.data);
          setPlayers(plRes.data);
        }
      } catch (err) { console.error("Failed to fetch highlights:", err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [playerName]);

  const filtered = useMemo(() => {
    return highlights.filter((h) => {
      const matchSearch = !search || h.title.toLowerCase().includes(search.toLowerCase()) || h.player_names?.some((n) => n.toLowerCase().includes(search.toLowerCase()));
      const matchType = eventFilter === "all" || h.event_type === eventFilter;
      return matchSearch && matchType;
    });
  }, [highlights, search, eventFilter]);

  const handleShare = async (clip) => {
    axios.post(`${API}/highlights/${clip.highlight_id}/share`).catch(() => {});
    const url = playerName
      ? `https://commonwealthfc.org/highlights/player/${encodeURIComponent(playerName)}`
      : `https://commonwealthfc.org/highlights`;
    if (navigator.share) {
      navigator.share({ title: clip.title, text: `Check out this highlight from Commonwealth FC: ${clip.title}`, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    }
  };

  const pageTitle = playerName ? `${playerName} Highlights` : "Player Highlights";
  const pageDesc = playerName
    ? `Watch ${playerName}'s best moments with Commonwealth FC youth soccer.`
    : "Watch Commonwealth FC player highlights — goals, assists, saves, and more from Charlottesville's community-first youth soccer club.";

  return (
    <div className="min-h-screen bg-[#F0F4F3]">
      <Helmet>
        <title>{pageTitle} | Commonwealth FC</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={`${pageTitle} | Commonwealth FC`} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={`https://commonwealthfc.org/highlights${playerName ? `/player/${encodeURIComponent(playerName)}` : ""}`} />
        <link rel="canonical" href={`https://commonwealthfc.org/highlights${playerName ? `/player/${encodeURIComponent(playerName)}` : ""}`} />
      </Helmet>

      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/highlights" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">All Highlights</Link>
            <Link to="/" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">Home</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          {playerName && (
            <Link to="/highlights" className="inline-flex items-center gap-1 text-xs text-[#6B8A8F] hover:text-[#0C2E3C] mb-4 transition-colors">
              <ChevronRight size={12} className="rotate-180" /> All Highlights
            </Link>
          )}
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0C2E3C] mb-2" data-testid="highlights-page-title">
            {playerName ? `${playerName.toUpperCase()}` : "PLAYER HIGHLIGHTS"}
          </h1>
          <p className="text-[#6B8A8F]">{playerName ? `${filtered.length} clips` : "Goals, assists, saves, and standout moments"}</p>
        </div>

        {!playerName && (
          <div className="flex flex-col sm:flex-row items-center gap-3 mb-8">
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B8A8F]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clips or players..." className="pl-9 rounded-none border-[#0C2E3C]/20" data-testid="highlights-search" />
            </div>
            <div className="flex items-center gap-1 overflow-x-auto">
              {[{ id: "all", label: "All" }, ...Object.entries(EVENT_TYPES).map(([id, cfg]) => ({ id, label: cfg.label }))].map((f) => (
                <button key={f.id} onClick={() => setEventFilter(f.id)} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${eventFilter === f.id ? "bg-[#0C2E3C] text-[#C8E64E]" : "text-[#6B8A8F] hover:text-[#0C2E3C]"}`} data-testid={`filter-${f.id}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0C2E3C]" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className={playerName ? "lg:col-span-4" : "lg:col-span-3"}>
              {filtered.length === 0 ? (
                <div className="text-center py-16">
                  <Film size={48} className="mx-auto text-[#6B8A8F]/30 mb-4" />
                  <p className="text-[#6B8A8F]">{playerName ? "No highlights yet for this player" : "No highlights yet — check back after game day!"}</p>
                </div>
              ) : (
                <div className={`grid gap-4 ${playerName ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
                  {filtered.map((clip) => (
                    <div key={clip.highlight_id} className="bg-white border border-[#0C2E3C]/10 overflow-hidden group hover:border-[#C8E64E] transition-colors" data-testid={`clip-${clip.highlight_id}`}>
                      {/* Video or thumbnail */}
                      {selectedClip === clip.highlight_id ? (
                        <VideoEmbed clip={clip} autoTrack />
                      ) : (
                        <button onClick={() => setSelectedClip(clip.highlight_id)} className="relative w-full pt-[56.25%] bg-[#0C2E3C] overflow-hidden">
                          {clip.thumbnail_url ? (
                            <img src={clip.thumbnail_url} alt={clip.title} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Film size={32} className="text-[#F0F4F3]/30" />
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-[#C8E64E] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                              <Play size={20} className="text-[#0C2E3C] ml-0.5" fill="#0C2E3C" />
                            </div>
                          </div>
                        </button>
                      )}
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-1">
                          {EVENT_TYPES[clip.event_type] && (
                            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${EVENT_TYPES[clip.event_type].color}`}>
                              {EVENT_TYPES[clip.event_type].label}
                            </span>
                          )}
                          {clip.game_info && <span className="text-[10px] text-[#6B8A8F]">{clip.game_info}</span>}
                        </div>
                        <h3 className="font-bold text-sm text-[#0C2E3C] leading-tight">{clip.title}</h3>
                        {clip.player_names?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {clip.player_names.map((name) => (
                              <Link key={name} to={`/highlights/player/${encodeURIComponent(name)}`} className="text-[10px] font-medium text-[#0C2E3C] bg-[#C8E64E]/20 px-1.5 py-0.5 rounded hover:bg-[#C8E64E]/40 transition-colors">
                                {name}
                              </Link>
                            ))}
                          </div>
                        )}
                        {clip.description && <p className="text-xs text-[#6B8A8F] mt-1 line-clamp-2">{clip.description}</p>}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#0C2E3C]/5">
                          <span className="text-[10px] text-[#6B8A8F]">{clip.views || 0} views</span>
                          <button onClick={() => handleShare(clip)} className="flex items-center gap-1 text-[10px] text-[#0C2E3C]/60 hover:text-[#C8E64E] transition-colors" data-testid={`share-${clip.highlight_id}`}>
                            <Share2 size={12} /> Share
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Players sidebar (only on main page) */}
            {!playerName && (
              <div>
                <h3 className="font-heading font-bold text-[#0C2E3C] text-sm uppercase tracking-wider mb-3">Players</h3>
                {players.length === 0 ? (
                  <p className="text-xs text-[#6B8A8F]">No players tagged yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="player-list">
                    {players.map((p) => (
                      <Link key={p.name} to={`/highlights/player/${encodeURIComponent(p.name)}`} className="flex items-center justify-between bg-white border border-[#0C2E3C]/10 px-3 py-2 hover:border-[#C8E64E] transition-colors" data-testid={`player-link-${p.name}`}>
                        <span className="text-sm text-[#0C2E3C] font-medium">{p.name}</span>
                        <span className="text-xs text-[#6B8A8F] font-bold">{p.clip_count} clips</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <footer className="bg-[#0C2E3C] mt-16 py-8 text-center">
        <p className="text-[#F0F4F3]/50 text-xs">Commonwealth FC &middot; Charlottesville, VA</p>
      </footer>
    </div>
  );
};

export default HighlightsPage;
